import { useState } from 'react'
import { useLanguage } from '@context/LanguageContext'
import { useDocumentMetadata } from '@hooks/useDocumentMetadata'
import { useDeviceType } from '@hooks/useDeviceType'
import { cx } from '@utils/cx'
import StaticLayout from '../layout/StaticLayout'
import { MailIcon, MailboxIcon, Twitter, Instagram, Facebook, Linkedin } from '../interface/Icons'
import { getItem, setItem } from '../../utils/storage'
import { useNamespaces } from '@hooks/useNamespaces'
import { ROUTE_NAMESPACES } from '@i18n/namespaces'
import ScreenFallback from '@components/layout/ScreenFallback'
import styles from './Contact.module.css'

function FallbackEmailHelper ({ feedback, onClose, t, copyToClipboard }) {
  if (!feedback || feedback.type !== 'fallback') return null

  return (
    <div className={cx('contact-fallback', styles.fallback)}>
      <p className={styles.fallbackMessage}>{feedback.message}</p>

      <div className={styles.fallbackDetails}>
        <div className={styles.fallbackRow}>
          <strong>{t.contact.helper.email}</strong>
          <button
            type='button'
            onClick={() => copyToClipboard(feedback.email)}
            className={styles.fallbackCopy}
          >
            {feedback.email} 📋
          </button>
        </div>

        {feedback.subject && (
          <div className={styles.fallbackRow}>
            <strong>{t.contact.helper.subject}</strong>
            <button
              type='button'
              onClick={() => copyToClipboard(feedback.subject)}
              className={styles.fallbackCopy}
            >
              {feedback.subject} 📋
            </button>
          </div>
        )}

        {feedback.body && (
          <>
            <div className={styles.fallbackRow}>
              <strong>{t.contact.helper.message}</strong>
            </div>
            <div className={styles.fallbackBody}>
              <textarea
                id='contact-fallback-textarea'
                name='message-preview'
                value={feedback.body}
                readOnly
                className={styles.fallbackTextarea}
              />
              <button
                type='button'
                onClick={() => copyToClipboard(feedback.body)}
                className={styles.fallbackCopy}
              >
                {t.contact.helper.clipboard} 📋
              </button>
            </div>
          </>
        )}
      </div>

      <button
        type='button'
        onClick={onClose}
        className={styles.fallbackClose}
      >
        {t.contact.helper.understood}
      </button>
    </div>
  )
}

export default function Contact () {
  const { t } = useLanguage()
  const { isMobile, isTabletVertical } = useDeviceType()
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [feedback, setFeedback] = useState(null)
  const [directFeedback, setDirectFeedback] = useState(null)
  const { isLoading: namespacesLoading } = useNamespaces(ROUTE_NAMESPACES.contact)
  useDocumentMetadata()

  if (namespacesLoading) {
    return <ScreenFallback />
  }

  // 🛡️ SANITIZE: Clean input to prevent injections
  const sanitizeInput = (input) => {
    return input
      .replace(/[\r\n\t]/g, ' ') // Normalize spaces
      .replace(/%0A|%0D|%09/gi, '') // Remove encoded characters
      .trim()
      .substring(0, 2000) // Maximum limit
  }

  // 🛡️ VALIDATIONS: Check content before sending
  const validateMessage = (msg) => {
    if (!msg.trim()) {
      return t.contact.errors.empty
    }
    if (msg.length < 10) {
      return t.contact.errors.tooShort
    }
    if (msg.length > 2000) {
      return t.contact.errors.tooLong
    }
    // 🛡️ SPAM DETECTION: Basic spam patterns
    if (/\b(viagra|casino|lottery|winner)\b/i.test(msg)) {
      return t.contact.errors.spam
    }
    return null
  }

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
      // Temporary clipboard feedback
      const originalFeedback = feedback
      setFeedback({ type: 'copied', message: t.contact.feedback.copied })
      setTimeout(() => setFeedback(originalFeedback), 2000)
    } catch (err) {
      // Clipboard API not available - silent fallback
    }
  }

  // 📧 HYBRID SUBMISSION: mailto with validations
  const handleSubmit = (e) => {
    e.preventDefault()

    const cleanMessage = sanitizeInput(message)
    const validationError = validateMessage(cleanMessage)

    if (validationError) {
      setError(validationError)
      return
    }

    // 🛡️ RATE LIMITING: Prevent repeated submissions
    const lastSubmit = getItem('lastContactSubmit')
    const now = Date.now()
    if (lastSubmit && (now - parseInt(lastSubmit, 10)) < 30000) {
      setError(t.contact.errors.rateLimit)
      return
    }
    setItem('lastContactSubmit', now)

    // 🔒 GENERATE MAILTO: Escape content safely
    const subject = encodeURIComponent(t.contact.email.subject)
    const body = encodeURIComponent(cleanMessage)
    const mailtoUrl = `mailto:contact@memodiez.com?subject=${subject}&body=${body}`

    // 🎯 DETECTION STRATEGY

    // Show immediate feedback
    setFeedback({ type: 'opening', message: t.contact.feedback.opening })
    setError('')

    // Open mailto link
    window.location.href = mailtoUrl

    // Detect if the mailto link was opened successfully
    const failureTimeout = setTimeout(() => {
      setFeedback({
        type: 'fallback',
        message: t.contact.feedback.fallback,
        email: 'contact@memodiez.com',
        subject: t.contact.email.subject,
        body: cleanMessage
      })
    }, 3000)

    // ✅ if it works, detect visibility change
    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearTimeout(failureTimeout)
        setFeedback({ type: 'success', message: t.contact.feedback.success })
        // Clear the message after successful submission
        setMessage('')
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange, { once: true })

    // Clear event listener after 5 seconds
    setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }, 5000)
  }

  const handleEmailDirect = () => {
    const mailtoUrl = 'mailto:contact@memodiez.com'

    // Show feedback
    setDirectFeedback({ type: 'opening', message: t.contact.feedback.opening })

    // Try mailto
    window.location.href = mailtoUrl

    // Timeout fallback
    const failureTimeout = setTimeout(() => {
      setDirectFeedback({
        type: 'fallback',
        message: t.contact.feedback.directFallback,
        email: 'contact@memodiez.com'
      })
    }, 3000)

    // Detect if it worked
    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearTimeout(failureTimeout)
        setDirectFeedback({ type: 'success', message: t.contact.feedback.success })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange, { once: true })

    setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }, 5000)
  }

  // 🌐 DEEP LINKS: Try native apps on mobile, web on desktop
  const getSocialUrl = (platform, handle) => {
    if (isMobile || isTabletVertical) {
      switch (platform) {
        case 'twitter': return `twitter://user?screen_name=${handle}`
        case 'instagram': return `instagram://user?username=${handle}`
        case 'facebook': return `https://facebook.com/${handle}`
        case 'linkedin': return 'https://linkedin.com/showcase/memodiez'
        default: return `https://${platform}.com/${handle}`
      }
    } else {
      return `https://${platform}.com/${handle}`
    }
  }

  const handleSocialClick = (platform, handle) => {
    const url = getSocialUrl(platform, handle)
    if (isMobile || isTabletVertical) {
      if (url.startsWith('https://')) {
        window.open(url, '_blank', 'noopener,noreferrer')
        return
      }

      window.location.href = url

      // Fallback to web after 2s
      setTimeout(() => {
        const webUrl = `https://${platform}.com/${handle}`
        window.open(webUrl, '_blank', 'noopener,noreferrer')
      }, 2000)
    } else {
      // 🖥️ DESKTOP: New tab
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  // Feedback type to style mapping
  const feedbackStyles = {
    opening: styles.feedbackOpening,
    success: styles.feedbackSuccess,
    copied: styles.feedbackCopied
  }

  return (
    <StaticLayout>
      <div className={styles.contactPage}>

        {/* 📧 MAIN TITLE */}
        <header className={styles.header}>
          <h1 className={styles.title}>{t.contact.title}</h1>
          <p className={styles.intro}>{t.contact.intro}</p>
        </header>

        {/* 📝 FORM SECTION */}
        <section className={styles.section} aria-labelledby='form-heading'>
          <h2 id='form-heading' className={styles.sectionTitle}>
            <MailIcon size={20} stroke={1.5} aria-hidden='true' />
            {t.contact.form.title}
          </h2>

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formField}>
              <label htmlFor='contact-message' className='sr-only'>
                {t.contact.form.label}
              </label>
              <textarea
                id='contact-message'
                name='message'
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t.contact.form.placeholder}
                maxLength={2000}
                required
                className={styles.textarea}
                aria-describedby='char-count error-message'
              />
              <div id='char-count' className={styles.charCount}>
                {message.length}/2000 {t.contact.form.characters}
              </div>
            </div>

            {error && (
              <div id='error-message' className='error-message' role='alert' aria-live='assertive'>
                {error}
              </div>
            )}

            <button type='submit' className={styles.submitBtn}>
              {t.contact.form.submit}
            </button>
          </form>

          {/* 💬 FORM FEEDBACK */}
          {feedback && feedback.type !== 'fallback' && (
            <div className={cx(styles.feedback, feedbackStyles[feedback.type])}>
              {feedback.message}
            </div>
          )}

          <FallbackEmailHelper
            feedback={feedback}
            onClose={() => setFeedback(null)}
            t={t}
            copyToClipboard={copyToClipboard}
          />
        </section>

        {/* 📬 DIRECT EMAIL SECTION */}
        <section className={styles.section} aria-labelledby='direct-heading'>
          <h2 id='direct-heading' className={styles.sectionTitle}>
            <MailboxIcon size={20} stroke={1.5} aria-hidden='true' />
            {t.contact.direct.title}
          </h2>
          <p className={styles.directText}>{t.contact.direct.text}</p>
          <button
            type='button'
            onClick={handleEmailDirect}
            className={styles.emailLink}
            aria-label={t.contact.access.emailButton}
          >
            contact@memodiez.com
          </button>
          <p className={styles.responseTime}>{t.contact.direct.response}</p>
          {/* 💬 DIRECT EMAIL FEEDBACK */}
          {directFeedback && directFeedback.type !== 'fallback' && (
            <div className={cx(styles.feedback, feedbackStyles[directFeedback.type])}>
              {directFeedback.message}
            </div>
          )}

          <FallbackEmailHelper
            feedback={directFeedback}
            onClose={() => setDirectFeedback(null)}
            t={t}
            copyToClipboard={copyToClipboard}
          />
        </section>

        {/* 🌐 SOCIAL MEDIA SECTION - Only mobile/tablet vertical */}
        {(isMobile || isTabletVertical) && (
          <section className={styles.section} aria-labelledby='social-heading'>
            <h2 id='social-heading' className={styles.sectionTitle}>
              🌐 {t.contact.social.title}
            </h2>

            <div className={styles.socialLinks}>
              <button
                onClick={() => handleSocialClick('twitter', 'memodiez_10')}
                className={styles.socialLink}
                aria-label={t.contact.social.twitterLabel}
              >
                <Twitter size={18} stroke={1.5} aria-hidden='true' />
                <strong>Twitter</strong>: @memodiez_10
              </button>

              <button
                onClick={() => handleSocialClick('instagram', 'memodiez10')}
                className={styles.socialLink}
                aria-label={t.contact.social.instagramLabel}
              >
                <Instagram size={18} stroke={1.5} aria-hidden='true' />
                <strong>Instagram</strong>: @memodiez10
              </button>

              <button
                onClick={() => handleSocialClick('facebook', 'memodiez10')}
                className={styles.socialLink}
                aria-label={t.contact.social.facebookLabel}
              >
                <Facebook size={18} stroke={1.5} aria-hidden='true' />
                <strong>Facebook</strong>: memodiez
              </button>

              <button
                onClick={() => handleSocialClick('linkedin', 'showcase/memodiez')}
                className={styles.socialLink}
                aria-label={t.contact.social.linkedinLabel}
              >
                <Linkedin size={18} stroke={1.5} aria-hidden='true' />
                <strong>LinkedIn</strong>: memodiez
              </button>
            </div>

            <p className={styles.socialText}>{t.contact.social.description}</p>
          </section>
        )}

        {/* 📝 DESCRIPTIVE FOOTER */}
        <footer className={styles.footer}>
          <p className={styles.footerText}>{t.contact.footer}</p>
        </footer>

      </div>
    </StaticLayout>
  )
}
